import React, { useState, useEffect } from "react";
import { Form, Button, Dropdown, Table, Icon, Checkbox, Label, Tab } from "semantic-ui-react";
import { toast, ToastContainer } from "react-toastify";
import { Accident, Division, Site, AccidentType, Sector, CiiuActivity } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { useCompany } from "../../../../contexts/CompanyContext";
import { OHS_STATUSES } from "../../../../utils/constants";
import "./AccidentForm.scss";

const accidentController     = new Accident();
const divisionController     = new Division();
const siteController         = new Site();
const accidentTypeController = new AccidentType();
const sectorController       = new Sector();
const ciiuActivityController = new CiiuActivity();

const BP_LEGACY = {
  "Cabeza": "bp_head", "Ojos": "bp_eyes", "Cara": "bp_face",
  "Cuello": "bp_neck", "Hombros": "bp_shoulders", "Brazos": "bp_arms",
  "Manos": "bp_hands", "Tronco/Espalda": "bp_trunk", "Caderas": "bp_hips",
  "Piernas": "bp_legs", "Rodillas": "bp_knees", "Tobillos": "bp_ankles",
  "Pies": "bp_feet", "Múltiples": "bp_multiple",
};
const CF_LEGACY = {
  "Persona": "cf_person", "Tarea": "cf_task",
  "Equipo/Máquina": "cf_equipment", "Entorno/Ambiente": "cf_environment",
  "Gestión/Organización": "cf_management",
};
const normalizeBP = (arr) => (arr || []).map(v => BP_LEGACY[v] || v);
const normalizeCF = (arr) => (arr || []).map(v => CF_LEGACY[v] || v);

const BODY_PARTS = [
  { key: "bp_head",      value: "bp_head"      },
  { key: "bp_eyes",      value: "bp_eyes"      },
  { key: "bp_face",      value: "bp_face"      },
  { key: "bp_neck",      value: "bp_neck"      },
  { key: "bp_shoulders", value: "bp_shoulders" },
  { key: "bp_arms",      value: "bp_arms"      },
  { key: "bp_hands",     value: "bp_hands"     },
  { key: "bp_trunk",     value: "bp_trunk"     },
  { key: "bp_hips",      value: "bp_hips"      },
  { key: "bp_legs",      value: "bp_legs"      },
  { key: "bp_knees",     value: "bp_knees"     },
  { key: "bp_ankles",    value: "bp_ankles"    },
  { key: "bp_feet",      value: "bp_feet"      },
  { key: "bp_multiple",  value: "bp_multiple"  },
];

const CONTRIBUTING_FACTORS = [
  { key: "cf_person",     value: "cf_person"     },
  { key: "cf_task",       value: "cf_task"       },
  { key: "cf_equipment",  value: "cf_equipment"  },
  { key: "cf_environment",value: "cf_environment"},
  { key: "cf_management", value: "cf_management" },
];

const ROOT_CAUSES_OPTIONS = [
  { key: "procedure_noncompliance", value: "procedure_noncompliance", text: "1. Incumplimiento de procedimientos / normas" },
  { key: "human_error",             value: "human_error",             text: "2. Error humano / Acción insegura" },
  { key: "equipment_maintenance",   value: "equipment_maintenance",   text: "3. Uso y mantenimiento deficiente de equipos / herramientas" },
  { key: "distraction",             value: "distraction",             text: "4. Distracción / Falta de concentración" },
  { key: "protection_failure",      value: "protection_failure",      text: "5. Falla o ausencia en sistemas de protección" },
  { key: "hazardous_exposure",      value: "hazardous_exposure",      text: "6. Exposición a agentes peligrosos (químicos, físicos, biológicos)" },
  { key: "unsafe_conditions",       value: "unsafe_conditions",       text: "7. Condiciones inseguras del lugar de trabajo (iluminación, orden)" },
  { key: "physical_capacity",       value: "physical_capacity",       text: "8. Estado físico / capacidad del trabajador" },
  { key: "mental_emotional",        value: "mental_emotional",        text: "9. Estado mental / emocional del trabajador" },
  { key: "unsafe_behavior",         value: "unsafe_behavior",         text: "10. Comportamiento inseguro / incumplimiento voluntario" },
  { key: "lack_technical_skills",   value: "lack_technical_skills",   text: "11. Falta de competencias técnicas" },
  { key: "insufficient_training",   value: "insufficient_training",   text: "12. Capacitación insuficiente o inadecuada" },
  { key: "inadequate_supervision",  value: "inadequate_supervision",  text: "13. Supervisión inadecuada" },
  { key: "safety_leadership",       value: "safety_leadership",       text: "14. Deficiencia en liderazgo de seguridad" },
  { key: "contractor_management",   value: "contractor_management",   text: "15. Mala gestión de contratistas / supervisión de terceros" },
  { key: "design_ergonomics",       value: "design_ergonomics",       text: "16. Deficiencia en diseño de instalaciones o ergonomía" },
  { key: "work_planning",           value: "work_planning",           text: "17. Falla en la planificación del trabajo / análisis de riesgos" },
  { key: "procurement",             value: "procurement",             text: "18. Gestión de compras y materiales deficiente" },
  { key: "ehs_policy",              value: "ehs_policy",              text: "19. Falta de implementación de políticas EHS" },
  { key: "communication",           value: "communication",           text: "20. Deficiencias en la comunicación" },
  { key: "risk_assessment",         value: "risk_assessment",         text: "21. Falta de evaluación de riesgos previa" },
  { key: "safety_culture",          value: "safety_culture",          text: "22. Falla en la cultura de seguridad" },
  { key: "overload_fatigue",        value: "overload_fatigue",        text: "23. Sobrecarga de trabajo / fatiga" },
  { key: "external_conditions",     value: "external_conditions",     text: "24. Condiciones externas (clima, tráfico, etc.)" },
  { key: "other",                   value: "other",                   text: "25. Otros — describa" },
];

const CLASSIFICATION_OPTIONS = [
  { key: "accident",   value: "accident",   text: "Accidente" },
  { key: "near_miss",  value: "near_miss",  text: "Casi accidente" },
  { key: "incident",   value: "incident",   text: "Incidente" },
  { key: "hazard",     value: "hazard",     text: "Condición peligrosa" },
];

const SEVERITY_OPTIONS = [
  { key: "mild",     value: "mild",     text: "Leve (sin días perdidos)" },
  { key: "moderate", value: "moderate", text: "Moderada (1–3 días perdidos)" },
  { key: "serious",  value: "serious",  text: "Grave (4–30 días perdidos)" },
  { key: "severe",   value: "severe",   text: "Muy grave (>30 días / secuela permanente)" },
  { key: "fatal",    value: "fatal",    text: "Fatal" },
];

const CASE_STATUS_OPTIONS = [
  { key: "open",     value: "open",     text: "Abierto" },
  { key: "closed",   value: "closed",   text: "Cerrado" },
  { key: "reopened", value: "reopened", text: "Reabierto" },
];

const SHIFT_OPTIONS = [
  { key: "morning_shift",   value: "morning_shift",   text: "Turno mañana" },
  { key: "afternoon_shift", value: "afternoon_shift", text: "Turno tarde" },
  { key: "night_shift",     value: "night_shift",     text: "Turno noche" },
  { key: "day_shift",       value: "day_shift",       text: "Jornada diurna" },
  { key: "rotating_shift",  value: "rotating_shift",  text: "Turno rotativo" },
  { key: "overtime",        value: "overtime",        text: "Horas extras" },
  { key: "break",           value: "break",           text: "Break / Descanso" },
  { key: "not_applicable",  value: "not_applicable",  text: "No aplica" },
];

const POSITION_TENURE_OPTIONS = [
  { key: "less_1m",   value: "less_1m",   text: "Menos de 1 mes" },
  { key: "1_3m",      value: "1_3m",      text: "1 a 3 meses" },
  { key: "3_6m",      value: "3_6m",      text: "3 a 6 meses" },
  { key: "6m_1y",     value: "6m_1y",     text: "6 meses a 1 año" },
  { key: "1_3y",      value: "1_3y",      text: "1 a 3 años" },
  { key: "more_3y",   value: "more_3y",   text: "Más de 3 años" },
];

const ESTABLISHMENT_SIZE_OPTIONS = [
  { key: "1-9",     value: "1-9",     text: "1–9 trabajadores" },
  { key: "10-49",   value: "10-49",   text: "10–49 trabajadores" },
  { key: "50-99",   value: "50-99",   text: "50–99 trabajadores" },
  { key: "100-249", value: "100-249", text: "100–249 trabajadores" },
  { key: "250-499", value: "250-499", text: "250–499 trabajadores" },
  { key: "500+",    value: "500+",    text: "≥500 trabajadores" },
];

const CONTRACT_TYPE_OPTIONS = [
  { key: "permanent",   value: "permanent",   text: "Planta permanente (relación de dependencia)" },
  { key: "fixed_term",  value: "fixed_term",  text: "Temporal / Plazo fijo" },
  { key: "intern",      value: "intern",      text: "Pasante / Becario / Practicante" },
  { key: "contractor",  value: "contractor",  text: "Contratista / Tercerizado" },
  { key: "agency",      value: "agency",      text: "Agencia / Temporal / Eventual" },
  { key: "freelance",   value: "freelance",   text: "Autónomo / Freelance" },
  { key: "other",       value: "other",       text: "Otro" },
];

const TOOL_CONDITION_OPTIONS = [
  { key: "ok",             value: "ok",             text: "Correctamente / En buen estado" },
  { key: "defective",      value: "defective",      text: "Defectuosa / En mal estado" },
  { key: "in_maintenance", value: "in_maintenance", text: "En mantenimiento al momento" },
  { key: "not_applicable", value: "not_applicable", text: "No aplica" },
];

const ACCIDENT_MECHANISM_OPTIONS = [
  { key: "fall_same_level",       value: "fall_same_level",       text: "Caída al mismo nivel" },
  { key: "fall_diff_level",       value: "fall_diff_level",       text: "Caída a distinto nivel" },
  { key: "slip",                  value: "slip",                  text: "Resbalón" },
  { key: "trip",                  value: "trip",                  text: "Tropezón" },
  { key: "hit_fixed_object",      value: "hit_fixed_object",      text: "Golpe contra objeto fijo" },
  { key: "hit_moving_object",     value: "hit_moving_object",     text: "Golpe contra objeto móvil" },
  { key: "struck_moving_object",  value: "struck_moving_object",  text: "Golpeado por objeto en movimiento" },
  { key: "struck_falling_object", value: "struck_falling_object", text: "Golpeado por objeto que cae" },
  { key: "collision",             value: "collision",             text: "Choque / colisión" },
  { key: "entrapment_objects",    value: "entrapment_objects",    text: "Atrapamiento entre objetos" },
  { key: "entrapment_parts",      value: "entrapment_parts",      text: "Atrapamiento por o entre partes móviles" },
  { key: "imprisonment",          value: "imprisonment",          text: "Aprisionamiento" },
  { key: "crushing",              value: "crushing",              text: "Aplastamiento" },
  { key: "cut",                   value: "cut",                   text: "Corte" },
  { key: "puncture",              value: "puncture",              text: "Punción" },
  { key: "prick",                 value: "prick",                 text: "Pinchazo" },
  { key: "laceration",            value: "laceration",            text: "Laceración" },
  { key: "amputation",            value: "amputation",            text: "Amputación" },
  { key: "overexertion",          value: "overexertion",          text: "Sobreesfuerzo físico" },
  { key: "overexertion_lifting",  value: "overexertion_lifting",  text: "Sobreesfuerzo por levantamiento manual de cargas" },
  { key: "overexertion_push",     value: "overexertion_push",     text: "Sobreesfuerzo por empuje o tracción" },
  { key: "repetitive_motion",     value: "repetitive_motion",     text: "Movimientos repetitivos" },
  { key: "forced_posture",        value: "forced_posture",        text: "Postura forzada" },
  { key: "hot_surface_contact",   value: "hot_surface_contact",   text: "Contacto con superficies calientes" },
  { key: "thermal_burn",          value: "thermal_burn",          text: "Quemadura térmica" },
  { key: "chemical_burn",         value: "chemical_burn",         text: "Quemadura química" },
  { key: "friction_burn",         value: "friction_burn",         text: "Quemadura por fricción" },
  { key: "electric_burn",         value: "electric_burn",         text: "Quemadura eléctrica" },
  { key: "direct_electrical",     value: "direct_electrical",     text: "Contacto eléctrico directo" },
  { key: "indirect_electrical",   value: "indirect_electrical",   text: "Contacto eléctrico indirecto" },
  { key: "electrocution",         value: "electrocution",         text: "Electrocución" },
  { key: "chemical_exposure",     value: "chemical_exposure",     text: "Exposición a sustancias químicas" },
  { key: "vapor_inhalation",      value: "vapor_inhalation",      text: "Inhalación de vapores, gases o polvos" },
  { key: "chemical_splash",       value: "chemical_splash",       text: "Salpicadura de sustancias químicas" },
  { key: "acute_poisoning",       value: "acute_poisoning",       text: "Intoxicación aguda" },
  { key: "asphyxia",              value: "asphyxia",              text: "Asfixia" },
  { key: "drowning",              value: "drowning",              text: "Ahogamiento" },
  { key: "biological_exposure",   value: "biological_exposure",   text: "Exposición a agentes biológicos" },
  { key: "sting",                 value: "sting",                 text: "Picadura" },
  { key: "bite",                  value: "bite",                  text: "Mordedura" },
  { key: "particle_projection",   value: "particle_projection",   text: "Proyección de partículas" },
  { key: "fragment_projection",   value: "fragment_projection",   text: "Proyección de fragmentos" },
  { key: "radiation_exposure",    value: "radiation_exposure",    text: "Exposición a radiación" },
  { key: "noise_exposure",        value: "noise_exposure",        text: "Exposición a ruido excesivo" },
  { key: "extreme_temp",          value: "extreme_temp",          text: "Exposición a temperaturas extremas" },
  { key: "fire",                  value: "fire",                  text: "Incendio" },
  { key: "explosion",             value: "explosion",             text: "Explosión" },
  { key: "vehicle_accident",      value: "vehicle_accident",      text: "Accidente con vehículo" },
  { key: "run_over",              value: "run_over",              text: "Atropellamiento" },
  { key: "rollover",              value: "rollover",              text: "Vuelco" },
  { key: "collapse",              value: "collapse",              text: "Derrumbe / colapso" },
  { key: "pressure_vessel",       value: "pressure_vessel",       text: "Explosión de recipiente a presión" },
  { key: "hazmat_release",        value: "hazmat_release",        text: "Fuga o liberación accidental de sustancias peligrosas" },
  { key: "hand_tool_accident",    value: "hand_tool_accident",    text: "Accidente con herramientas manuales" },
  { key: "machinery_accident",    value: "machinery_accident",    text: "Accidente con maquinaria" },
  { key: "forklift_accident",     value: "forklift_accident",     text: "Accidente con montacargas / autoelevador" },
  { key: "confined_space",        value: "confined_space",        text: "Accidente en espacios confinados" },
  { key: "maintenance_loto",      value: "maintenance_loto",      text: "Accidente durante mantenimiento / LOTO" },
  { key: "workplace_violence",    value: "workplace_violence",    text: "Agresión / violencia laboral" },
  { key: "other",                 value: "other",                 text: "Otro" },
];

const RECORD_STATUS_OPTIONS = OHS_STATUSES.map(s => ({ key: s.key, value: s.value }));

const CAPA_TYPE_OPTIONS = [
  { key: "corrective",  value: "corrective",  text: "Correctiva" },
  { key: "preventive",  value: "preventive",  text: "Preventiva" },
];

const CAPA_STATUS_OPTIONS = OHS_STATUSES.map(s => ({ key: s.key, value: s.value }));

const EFFICACY_OPTIONS = [
  { key: "effective",           value: "effective",           text: "Eficaz" },
  { key: "partially_effective", value: "partially_effective", text: "Parcialmente eficaz" },
  { key: "ineffective",         value: "ineffective",         text: "No eficaz" },
  { key: "not_evaluated",       value: "not_evaluated",       text: "No evaluada" },
];

const METHODOLOGY_OPTIONS = [
  { key: "5_whys",    value: "5_whys",    text: "5 Porques" },
  { key: "fishbone",  value: "fishbone",  text: "Ishikawa / Espina de pescado" },
  { key: "other",     value: "other",     text: "Otro" },
];

const EMPTY_ACTION   = { type: "corrective", description: "", responsible: "", dueDate: "", status: "pending", evidence: "", efficacy: "" };
const EMPTY_WITNESS  = { name: "", signature: "", testimony: "" };

function SectionCard({ title, children }) {
  return (
    <div className="accident-section-card">
      <h3 className="accident-section-title">{title}</h3>
      {children}
    </div>
  );
}

function TagInput({ values, onChange, placeholder }) {
  const [input, setInput] = useState("");
  const handleKey = (e) => {
    if (e.key === "Enter" && input.trim()) {
      e.preventDefault();
      onChange([...values, input.trim()]);
      setInput("");
    }
  };
  const remove = (i) => onChange(values.filter((_, idx) => idx !== i));
  return (
    <div className="tag-input-wrapper">
      {values.map((v, i) => (
        <Label key={i} style={{ marginBottom: "4px" }}>
          {v} <Icon name="delete" onClick={() => remove(i)} style={{ cursor: "pointer" }} />
        </Label>
      ))}
      <input
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={handleKey}
        placeholder={placeholder}
        style={{ border: "none", outline: "none", minWidth: "180px", marginTop: "4px" }}
      />
    </div>
  );
}

export function AccidentForm({ accident, close, onReload }) {
  const { accessToken } = useAuth();
  const { company: companyFromContext } = useCompany();
  const noCompanyInCtx = !companyFromContext || companyFromContext === "0" || companyFromContext === 0;
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [saving, setSaving] = useState(false);
  const [divisions,       setDivisions]       = useState([]);
  const [sites,           setSites]           = useState([]);
  const [accidentTypes,   setAccidentTypes]   = useState([]);
  const [sectors,         setSectors]         = useState([]);
  const [ciiuActivities,  setCiiuActivities]  = useState([]);

  // New CAPA / witness form state
  const [newAction,  setNewAction]  = useState({ ...EMPTY_ACTION });
  const [newWitness, setNewWitness] = useState({ ...EMPTY_WITNESS });

  const [form, setForm] = useState({
    // Tab 1 · Establecimiento — Empleador
    employerName:         accident?.employerName         || "",
    cuit:                 accident?.cuit                 || "",
    legalAddress:         accident?.legalAddress         || "",
    employerPhone:        accident?.employerPhone        || "",
    // Tab 1 · Establecimiento — Establecimiento físico
    establishmentName:    accident?.establishmentName    || "",
    establishmentAddress: accident?.establishmentAddress || "",
    economicActivity:     accident?.economicActivity     || "",
    establishmentSize:    accident?.establishmentSize    || "",
    // Tab 1 · Localización interna
    division:         accident?.division?._id    || accident?.division    || "",
    site:             accident?.site?._id        || accident?.site        || "",
    sector:           accident?.sector?._id      || accident?.sector      || "",
    location:         accident?.location         || "",
    date:             accident?.date ? accident.date.slice(0, 10) : "",
    time:             accident?.time             || "",
    shift:            accident?.shift            || "",
    // process:          accident?.process          || "",
    accidentType:     accident?.accidentType?._id|| accident?.accidentType|| "",
    classification:   accident?.classification   || "",
    initialSeverity:  accident?.initialSeverity  || "",
    briefDescription: accident?.briefDescription || "",
    immediateCause:   accident?.immediateCause   || "",
    involvedPersons:  accident?.involvedPersons  || "",
    firstAid:         accident?.firstAid         || "",
    referral:         accident?.referral         || "",
    artInsurance:     accident?.artInsurance     || "",
    lostDays:         accident?.lostDays         ?? 0,
    legajo:           accident?.legajo           || "",
    employee:         accident?.employee         || "",
    seniority:        accident?.seniority        || "",
    contractType:     accident?.contractType     || "",
    toolCondition:        accident?.toolCondition        || "",
    accidentMechanism:    accident?.accidentMechanism    || "",
    accidentMechanismOther: accident?.accidentMechanismOther || "",

    bodyParts:     normalizeBP(accident?.bodyParts),
    impactedAreas: accident?.impactedAreas || [],

    generateActionPlan: accident?.generateActionPlan ?? false,
    actionPlanNote:     accident?.actionPlanNote     || "",
    recordStatus:       accident?.recordStatus       || "",
    caseStatus:         accident?.caseStatus         || "open",
    reopenDate:   accident?.reopenDate   ? accident.reopenDate.slice(0, 10) : "",
    reopenReason: accident?.reopenReason || "",
    correctiveActions: accident?.correctiveActions || [],

    rootCause: {
      narrative:           accident?.rootCause?.narrative           || accident?.briefDescription || "",
      priorConditions:     accident?.rootCause?.priorConditions     || "",
      permitsEPP:          accident?.rootCause?.permitsEPP          || "",
      methodology:         accident?.rootCause?.methodology         || "5_whys",
      whys:                accident?.rootCause?.whys                || ["", "", "", "", ""],
      immediateCauses:     accident?.rootCause?.immediateCauses     || "",
      underlyingCauses:    accident?.rootCause?.underlyingCauses    || "",
      latentCauses:        accident?.rootCause?.latentCauses        || "",
      contributingFactors: normalizeCF(accident?.rootCause?.contributingFactors),
      causes:              accident?.rootCause?.causes              || [],
      lessonLearned:       accident?.rootCause?.lessonLearned       || "",
    },

    witnesses: accident?.witnesses || [],

    lessons: {
      keyLesson:      accident?.lessons?.keyLesson      || "",
      trainings:      accident?.lessons?.trainings      || [],
      riskMatrix:     accident?.lessons?.riskMatrix     || [],
      diffusionAreas: accident?.lessons?.diffusionAreas || "",
      diffusionDate:  accident?.lessons?.diffusionDate  ? accident.lessons.diffusionDate.slice(0, 10) : "",
    },

    metrics: {
      estimatedLostTime: accident?.metrics?.estimatedLostTime || "",
      directCosts:       accident?.metrics?.directCosts       ?? 0,
      indirectCosts:     accident?.metrics?.indirectCosts     ?? 0,
      confirmedLostDays: accident?.metrics?.confirmedLostDays ?? 0,
    },
  });

  useEffect(() => {
    (async () => {
      try {
        const sitesRequest = noCompanyInCtx
          ? siteController.getSites(accessToken, true)
          : siteController.getSitesByCompany(accessToken, companyFromContext, true);
        const [divs, sts, types, secs, ciius] = await Promise.all([
          divisionController.getDivisions(accessToken, true),
          sitesRequest,
          accidentTypeController.getAccidentTypes(accessToken, true),
          sectorController.getSectors(accessToken, true),
          ciiuActivityController.getCiiuActivities(accessToken, true),
        ]);
        setDivisions(divs.map(d => ({ key: d._id, value: d._id, text: d.name })));
        setSites(sts.map(s => ({ key: s._id, value: s._id, text: s.name })));
        setAccidentTypes(types.map(a => ({ key: a._id, value: a._id, text: a.name, country: a.country || "" })));
        setSectors(secs.map(s => ({ key: s._id, value: s._id, text: s.name })));
        setCiiuActivities(ciius.map(c => ({ key: c._id, value: `${c.code} · ${c.name}`, text: `${c.code} · ${c.name}` })));
      } catch { /* ignore */ }
    })();
  }, [accessToken, companyFromContext, noCompanyInCtx]);

  const set = (field, value) => setForm(p => ({ ...p, [field]: value }));
  const setRootCause = (field, value) => setForm(p => ({ ...p, rootCause: { ...p.rootCause, [field]: value } }));
  const setLessons   = (field, value) => setForm(p => ({ ...p, lessons:   { ...p.lessons,   [field]: value } }));
  const setMetrics   = (field, value) => setForm(p => ({ ...p, metrics:   { ...p.metrics,   [field]: value } }));

  const toggleBodyPart = (part) => {
    const current = form.bodyParts;
    set("bodyParts", current.includes(part) ? current.filter(p => p !== part) : [...current, part]);
  };

  const toggleFactor = (factor) => {
    const current = form.rootCause.contributingFactors;
    setRootCause("contributingFactors", current.includes(factor) ? current.filter(f => f !== factor) : [...current, factor]);
  };

  const setWhy = (i, val) => {
    const whys = [...form.rootCause.whys];
    whys[i] = val;
    setRootCause("whys", whys);
  };

  // CAPA actions
  const addAction = () => {
    if (!newAction.description) return;
    set("correctiveActions", [...form.correctiveActions, { ...newAction }]);
    setNewAction({ ...EMPTY_ACTION });
  };
  const removeAction = (i) => set("correctiveActions", form.correctiveActions.filter((_, idx) => idx !== i));

  // Witnesses
  const addWitness = () => {
    if (!newWitness.name) return;
    set("witnesses", [...form.witnesses, { ...newWitness }]);
    setNewWitness({ ...EMPTY_WITNESS });
  };
  const removeWitness = (i) => set("witnesses", form.witnesses.filter((_, idx) => idx !== i));

  const handleSubmit = async () => {
    if (!form.date) { toast.error(t("date_required")); return; }
    setSaving(true);
    try {
      if (accident) {
        await accidentController.updateAccident(accessToken, accident._id, form);
        toast.success(t("accident_updated"));
      } else {
        await accidentController.createAccident(accessToken, form);
        toast.success(t("accident_created"));
      }
      onReload();
      close();
    } catch (err) {
      toast.error(err?.msg || t("error_saving"));
    } finally {
      setSaving(false);
    }
  };

  const panes = [
    {
      menuItem: t("acc_tab_establishment"),
      render: () => (
        <Tab.Pane>
          <p style={{ fontSize: "0.82rem", color: "#6B7280", marginBottom: "1rem" }}>
            Datos legales del empleador y de la instalación física. Si el sitio ya está en el sistema, varios campos se auto-completan.
          </p>

          <div className="accident-section-card">
            <div className="accident-section-title">Empleador</div>
            <Form>
              <Form.Group widths="equal">
                <Form.Field>
                  <label>Razón social <span style={{ color: "#DC2626" }}>*</span></label>
                  <input value={form.employerName} onChange={e => set("employerName", e.target.value)} />
                </Form.Field>
                <Form.Input label="CUIT" value={form.cuit} placeholder="XX-XXXXXXXXX-X"
                  onChange={e => set("cuit", e.target.value)} />
                <Form.Input label="Dirección legal" value={form.legalAddress}
                  onChange={e => set("legalAddress", e.target.value)} />
                <Form.Input label="Teléfono" type="tel" value={form.employerPhone} placeholder="+54 11 XXXX-XXXX"
                  onChange={e => set("employerPhone", e.target.value)} />
              </Form.Group>
            </Form>
          </div>

          <div className="accident-section-card">
            <div className="accident-section-title">Establecimiento donde ocurrió</div>
            <Form>
              <Form.Group widths="equal">
                <Form.Field>
                  <label>Nombre del establecimiento <span style={{ color: "#DC2626" }}>*</span></label>
                  <input value={form.establishmentName} onChange={e => set("establishmentName", e.target.value)}
                    placeholder="Ej: Centro de distribución Pilar" />
                </Form.Field>
                <Form.Input label="Dirección" value={form.establishmentAddress}
                  onChange={e => set("establishmentAddress", e.target.value)} />
              </Form.Group>
              <Form.Group>
                <Form.Field width={10}>
                  <label>Actividad económica (CIIU Rev.4)</label>
                  <Dropdown
                    fluid selection search clearable
                    options={ciiuActivities}
                    value={form.economicActivity}
                    onChange={(_, { value }) => set("economicActivity", value)}
                    placeholder="Seleccionar código CIIU"
                    noResultsMessage="Sin resultados"
                  />
                </Form.Field>
                <Form.Field width={6}>
                  <label>Tamaño establecimiento <span style={{ color: "#DC2626" }}>*</span></label>
                  <Dropdown fluid selection options={ESTABLISHMENT_SIZE_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={form.establishmentSize}
                    onChange={(_, { value }) => set("establishmentSize", value)} placeholder={t("select")} clearable />
                </Form.Field>
              </Form.Group>
            </Form>
          </div>

          <div className="accident-section-card">
            <div className="accident-section-title">Localización interna</div>
            <Form>
              <Form.Group widths="equal">
                <Form.Field>
                  <label>{t("division")}</label>
                  <Dropdown fluid selection options={divisions} value={form.division}
                    onChange={(_, { value }) => set("division", value)} placeholder={t("select")} clearable />
                </Form.Field>
                <Form.Field>
                  <label>{t("site")}</label>
                  <Dropdown fluid selection options={sites} value={form.site}
                    onChange={(_, { value }) => set("site", value)} placeholder={t("select")} clearable />
                </Form.Field>
                <Form.Field>
                  <label>{t("sector")} / Área</label>
                  <Dropdown fluid selection options={sectors} value={form.sector}
                    onChange={(_, { value }) => set("sector", value)} placeholder={t("select")} clearable />
                </Form.Field>
                <Form.Input label="Lugar específico" value={form.location}
                  onChange={e => set("location", e.target.value)}
                  placeholder="Ej: Pasillo 3, estiba alta" />
              </Form.Group>
            </Form>
          </div>
        </Tab.Pane>
      ),
    },
    {
      menuItem: t("basic_accident_data"),
      render: () => (
        <Tab.Pane>
          <Form>
            <Form.Group widths="equal">
              <Form.Input label={<>Fecha <span style={{ color: "#DC2626" }}>*</span></>} type="date" value={form.date}
                onChange={e => set("date", e.target.value)} />
              <Form.Input label={t("time_hs")} type="time" value={form.time}
                onChange={e => set("time", e.target.value)} />
              <Form.Field>
                <label>Turno en que ocurrió</label>
                <Dropdown fluid selection options={SHIFT_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={form.shift}
                  onChange={(_, { value }) => set("shift", value)} placeholder={t("select")} clearable />
              </Form.Field>
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input label={t("legajo")} value={form.legajo}
                onChange={e => set("legajo", e.target.value)} />
              <Form.Input label={t("employee")} value={form.employee}
                onChange={e => set("employee", e.target.value)} />
              <Form.Field>
                <label>Años de antigüedad</label>
                <Dropdown fluid selection options={POSITION_TENURE_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={form.seniority}
                  onChange={(_, { value }) => set("seniority", value)} placeholder={t("select")} clearable />
              </Form.Field>
              <Form.Field>
                <label>Tipo de contrato</label>
                <Dropdown fluid selection options={CONTRACT_TYPE_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={form.contractType}
                  onChange={(_, { value }) => set("contractType", value)} placeholder={t("select")} clearable />
              </Form.Field>
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Field>
                <label>{t("accident_type")}</label>
                <Dropdown fluid selection value={form.accidentType}
                  options={accident?.site?.country
                    ? accidentTypes.filter(a => !a.country || a.country === accident.site.country)
                    : accidentTypes}
                  onChange={(_, { value }) => set("accidentType", value)} placeholder={t("select")} />
              </Form.Field>
              <Form.Field>
                <label>{t("classification")}</label>
                <Dropdown fluid selection options={CLASSIFICATION_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={form.classification}
                  onChange={(_, { value }) => set("classification", value)} placeholder={t("select")} />
              </Form.Field>
              <Form.Field>
                <label>{t("injury_severity")}</label>
                <Dropdown fluid selection options={SEVERITY_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={form.initialSeverity}
                  onChange={(_, { value }) => set("initialSeverity", value)} placeholder={t("select")} />
              </Form.Field>
            </Form.Group>
            <Form.Group widths="equal">
              <Form.TextArea label={t("brief_description")} value={form.briefDescription} rows={3}
                onChange={e => set("briefDescription", e.target.value)} />
              <Form.TextArea label={t("involved_persons")} value={form.involvedPersons} rows={3}
                onChange={e => set("involvedPersons", e.target.value)} />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.TextArea label={t("first_aid")} value={form.firstAid} rows={2}
                onChange={e => set("firstAid", e.target.value)} />
              <Form.Input label={t("referral")} value={form.referral}
                onChange={e => set("referral", e.target.value)} />
              <Form.Input label={t("art_insurance")} value={form.artInsurance}
                onChange={e => set("artInsurance", e.target.value)} />
              <Form.Input label={`${t("lost_days")} *`} type="number" value={form.lostDays} min={0}
                onChange={e => set("lostDays", Number(e.target.value))} />
            </Form.Group>
          </Form>
        </Tab.Pane>
      ),
    },
    {
      menuItem: t("acc_tab_mechanism"),
      render: () => (
        <Tab.Pane>
          <Form>
            <Form.TextArea label="Causa inmediata del accidente" value={form.immediateCause} rows={4}
              onChange={e => set("immediateCause", e.target.value)} />
            <Form.Group widths="equal" style={{ marginTop: "1rem" }}>
              <Form.Field>
                <label>{t("tool_condition")}</label>
                <Dropdown fluid selection options={TOOL_CONDITION_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={form.toolCondition}
                  onChange={(_, { value }) => set("toolCondition", value)} placeholder={t("select")} clearable />
              </Form.Field>
              <Form.Field>
                <label>{t("accident_mechanism")}</label>
                <Dropdown fluid selection search options={ACCIDENT_MECHANISM_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={form.accidentMechanism}
                  onChange={(_, { value }) => set("accidentMechanism", value)} placeholder={t("select")} clearable />
              </Form.Field>
              {form.accidentMechanism === "other" && (
                <Form.Input
                  label={t("specify")}
                  value={form.accidentMechanismOther}
                  onChange={e => set("accidentMechanismOther", e.target.value)}
                  placeholder={t("specify")}
                />
              )}
            </Form.Group>
          </Form>
        </Tab.Pane>
      ),
    },
    {
      menuItem: t("accident_impact"),
      render: () => (
        <Tab.Pane>
          <p style={{ fontSize: "0.85rem", color: "#555", marginBottom: "0.6rem" }}>{t("select_body_parts")}</p>
          <div className="body-parts-grid">
            {BODY_PARTS.map(part => (
              <Button key={part.value} size="small" type="button"
                className={`body-part-btn${form.bodyParts.includes(part.value) ? " selected" : ""}`}
                onClick={() => toggleBodyPart(part.value)}>
                {t(part.value)}
              </Button>
            ))}
          </div>
          <p style={{ marginTop: "0.5rem", fontSize: "0.85rem" }}>
            <strong>{t("selected_parts")}:</strong>{" "}
            {form.bodyParts.length > 0 ? form.bodyParts.map(v => t(v)).join(", ") : t("none")}
          </p>
          <Form style={{ marginTop: "1rem" }}>
            <Form.Field>
              <label>{t("impacted_areas")}</label>
              <TagInput
                values={form.impactedAreas}
                onChange={v => set("impactedAreas", v)}
                placeholder={t("type_and_enter")}
              />
            </Form.Field>
          </Form>
        </Tab.Pane>
      ),
    },
    {
      menuItem: t("case_status_followup"),
      render: () => (
        <Tab.Pane>
          <Form>
            <Form.Group widths="equal">
              <Form.Field>
                <label>{t("case_status")}</label>
                <Dropdown fluid selection options={CASE_STATUS_OPTIONS.map(o => ({...o, text: t("case_" + o.value)}))} value={form.caseStatus}
                  onChange={(_, { value }) => set("caseStatus", value)} />
              </Form.Field>
              <Form.Input label={t("reopen_date")} type="date" value={form.reopenDate}
                onChange={e => set("reopenDate", e.target.value)}
                disabled={form.caseStatus !== "reopened"}
                style={form.caseStatus !== "reopened" ? { opacity: 0.4 } : {}} />
              <Form.Input label={t("reopen_reason")} value={form.reopenReason}
                onChange={e => set("reopenReason", e.target.value)}
                disabled={form.caseStatus !== "reopened"}
                style={form.caseStatus !== "reopened" ? { opacity: 0.4 } : {}} />
            </Form.Group>
          </Form>

          <h4 style={{ marginTop: "1.2rem" }}>{t("corrective_actions")} (CAPA)</h4>
          <Form>
            <Form.Group>
              <Form.Field width={3}>
                <label>{t("type")}</label>
                <Dropdown fluid selection options={CAPA_TYPE_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={newAction.type}
                  onChange={(_, { value }) => setNewAction(p => ({ ...p, type: value }))} />
              </Form.Field>
              <Form.Input width={4} label={t("description")} value={newAction.description}
                onChange={e => setNewAction(p => ({ ...p, description: e.target.value }))} />
              <Form.Input width={3} label={t("responsible")} value={newAction.responsible}
                onChange={e => setNewAction(p => ({ ...p, responsible: e.target.value }))} />
              <Form.Input width={3} label={t("due_date")} type="date" value={newAction.dueDate}
                onChange={e => setNewAction(p => ({ ...p, dueDate: e.target.value }))} />
              <Form.Field width={3}>
                <label>{t("status")}</label>
                <Dropdown fluid selection options={CAPA_STATUS_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={newAction.status}
                  onChange={(_, { value }) => setNewAction(p => ({ ...p, status: value }))} />
              </Form.Field>
            </Form.Group>
            <Form.Group>
              <Form.Input width={6} label={t("evidence_url")} value={newAction.evidence}
                onChange={e => setNewAction(p => ({ ...p, evidence: e.target.value }))} placeholder="https://..." />
              <Form.Field width={4}>
                <label>{t("efficacy")}</label>
                <Dropdown fluid selection options={EFFICACY_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={newAction.efficacy}
                  onChange={(_, { value }) => setNewAction(p => ({ ...p, efficacy: value }))} />
              </Form.Field>
              <Form.Field width={2} style={{ display: "flex", alignItems: "flex-end" }}>
                <Button primary onClick={addAction} type="button">{t("add_action")}</Button>
              </Form.Field>
            </Form.Group>
          </Form>
          {form.correctiveActions.length === 0 ? (
            <p style={{ color: "#999", fontSize: "0.85rem" }}>{t("no_actions_loaded")}</p>
          ) : (
            <Table compact size="small" style={{ marginTop: "0.5rem" }}>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>{t("type")}</Table.HeaderCell>
                  <Table.HeaderCell>{t("description")}</Table.HeaderCell>
                  <Table.HeaderCell>{t("responsible")}</Table.HeaderCell>
                  <Table.HeaderCell>{t("due_date")}</Table.HeaderCell>
                  <Table.HeaderCell>{t("status")}</Table.HeaderCell>
                  <Table.HeaderCell>{t("evidence_url")}</Table.HeaderCell>
                  <Table.HeaderCell>{t("efficacy")}</Table.HeaderCell>
                  <Table.HeaderCell />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {form.correctiveActions.map((a, i) => (
                  <Table.Row key={i}>
                    <Table.Cell>{t(a.type) || a.type}</Table.Cell>
                    <Table.Cell>{a.description}</Table.Cell>
                    <Table.Cell>{a.responsible}</Table.Cell>
                    <Table.Cell>{a.dueDate ? new Date(a.dueDate).toLocaleDateString() : "-"}</Table.Cell>
                    <Table.Cell>{t("case_" + a.status) || a.status}</Table.Cell>
                    <Table.Cell>{a.evidence || "-"}</Table.Cell>
                    <Table.Cell>{t(a.efficacy) || "-"}</Table.Cell>
                    <Table.Cell>
                      <Button icon size="mini" color="red" type="button" onClick={() => removeAction(i)}>
                        <Icon name="trash" />
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </Tab.Pane>
      ),
    },
    {
      menuItem: t("root_cause_analysis"),
      render: () => (
        <Tab.Pane>
          <Form>
            <Form.Group widths="equal">
              <Form.TextArea label={t("accident_narrative")} value={form.rootCause.narrative} rows={3}
                onChange={e => setRootCause("narrative", e.target.value)} />
              <Form.TextArea label={t("prior_conditions")} value={form.rootCause.priorConditions} rows={3}
                onChange={e => setRootCause("priorConditions", e.target.value)} />
            </Form.Group>
            <Form.TextArea label={t("permits_epp")} value={form.rootCause.permitsEPP} rows={2}
              onChange={e => setRootCause("permitsEPP", e.target.value)} />
            <Form.Group>
              <Form.Field width={6}>
                <label>{t("methodology")}</label>
                <Dropdown fluid selection options={METHODOLOGY_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={form.rootCause.methodology}
                  onChange={(_, { value }) => setRootCause("methodology", value)} />
              </Form.Field>
            </Form.Group>
            {form.rootCause.methodology === "5_whys" && (
              <Form.Group widths="equal">
                {[0,1,2,3,4].map(i => (
                  <Form.TextArea key={i} label={`¿Por qué ${i+1}?`} value={form.rootCause.whys[i] || ""} rows={2}
                    onChange={e => setWhy(i, e.target.value)} />
                ))}
              </Form.Group>
            )}
            <Form.Group widths="equal" style={{ marginTop: "0.8rem" }}>
              <Form.TextArea label={t("immediate_causes")} value={form.rootCause.immediateCauses} rows={3}
                onChange={e => setRootCause("immediateCauses", e.target.value)} />
              <Form.TextArea label={t("underlying_causes")} value={form.rootCause.underlyingCauses} rows={3}
                onChange={e => setRootCause("underlyingCauses", e.target.value)} />
              <Form.TextArea label={t("latent_causes")} value={form.rootCause.latentCauses} rows={3}
                onChange={e => setRootCause("latentCauses", e.target.value)} />
            </Form.Group>
            <Form.Field>
              <label>{t("root_cause")}</label>
              <Dropdown fluid multiple selection options={ROOT_CAUSES_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={form.rootCause.causes}
                onChange={(_, { value }) => setRootCause("causes", value)}
                placeholder={t("select_root_causes")} />
            </Form.Field>
            <Form.Field>
              <label>{t("contributing_factors")}</label>
              <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap", marginTop: "0.4rem" }}>
                {CONTRIBUTING_FACTORS.map(f => (
                  <Checkbox key={f.value} label={t(f.value)} checked={form.rootCause.contributingFactors.includes(f.value)}
                    onChange={() => toggleFactor(f.value)} />
                ))}
              </div>
            </Form.Field>
            <Form.TextArea label="Lección aprendida" value={form.rootCause.lessonLearned} rows={3}
              onChange={e => setRootCause("lessonLearned", e.target.value)} />
            <Form.Group widths="equal" style={{ marginTop: "1rem" }}>
              <Form.Field>
                <label>{t("generate_action_plan")}</label>
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginTop: "0.3rem" }}>
                  <Checkbox toggle checked={form.generateActionPlan}
                    onChange={(_, { checked }) => set("generateActionPlan", checked)} />
                  {form.generateActionPlan && (
                    <input type="text" value={form.actionPlanNote}
                      onChange={e => set("actionPlanNote", e.target.value)}
                      placeholder={t("action_plan_note_placeholder")}
                      style={{ flex: 1, padding: "6px 10px", borderRadius: "6px", border: "1px solid rgba(34,36,38,0.2)", fontSize: "0.9rem" }} />
                  )}
                </div>
              </Form.Field>
              <Form.Field>
                <label>{t("record_status")}</label>
                <Dropdown fluid selection options={RECORD_STATUS_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={form.recordStatus}
                  onChange={(_, { value }) => set("recordStatus", value)} placeholder={t("select")} clearable />
              </Form.Field>
            </Form.Group>
          </Form>

          <h4 style={{ marginTop: "1.5rem" }}>{t("witnesses")}</h4>
          <Form>
            <Form.Group widths="equal">
              <Form.Input label={t("witness_name")} value={newWitness.name}
                onChange={e => setNewWitness(p => ({ ...p, name: e.target.value }))} />
              <Form.TextArea label={t("testimony")} value={newWitness.testimony} rows={2}
                onChange={e => setNewWitness(p => ({ ...p, testimony: e.target.value }))} />
            </Form.Group>
            <Button type="button" size="small" onClick={addWitness}>{t("add_witness")}</Button>
          </Form>
          {form.witnesses.length === 0 ? (
            <p style={{ color: "#999", fontSize: "0.85rem", marginTop: "0.5rem" }}>{t("no_witnesses")}</p>
          ) : (
            <Table compact size="small" style={{ marginTop: "0.5rem" }}>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>{t("witness_name")}</Table.HeaderCell>
                  <Table.HeaderCell>{t("testimony")}</Table.HeaderCell>
                  <Table.HeaderCell />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {form.witnesses.map((w, i) => (
                  <Table.Row key={i}>
                    <Table.Cell>{w.name}</Table.Cell>
                    <Table.Cell>{w.testimony}</Table.Cell>
                    <Table.Cell>
                      <Button icon size="mini" color="red" type="button" onClick={() => removeWitness(i)}>
                        <Icon name="trash" />
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </Tab.Pane>
      ),
    },
    {
      menuItem: t("lessons_learned"),
      render: () => (
        <Tab.Pane>
          <Form>
            <Form.TextArea label={t("key_lesson")} value={form.lessons.keyLesson} rows={3}
              onChange={e => setLessons("keyLesson", e.target.value)} />
            <Form.Group widths="equal">
              <Form.Field>
                <label>{t("link_trainings")}</label>
                <TagInput values={form.lessons.trainings}
                  onChange={v => setLessons("trainings", v)} placeholder={t("type_and_enter")} />
              </Form.Field>
              <Form.Field>
                <label>{t("link_risk_matrix")}</label>
                <TagInput values={form.lessons.riskMatrix}
                  onChange={v => setLessons("riskMatrix", v)} placeholder={t("type_and_enter")} />
              </Form.Field>
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input label={t("diffusion_areas")} value={form.lessons.diffusionAreas}
                onChange={e => setLessons("diffusionAreas", e.target.value)}
                placeholder={t("separate_by_comma")} />
              <Form.Input label={t("diffusion_date")} type="date" value={form.lessons.diffusionDate}
                onChange={e => setLessons("diffusionDate", e.target.value)} />
            </Form.Group>
          </Form>
        </Tab.Pane>
      ),
    },
    {
      menuItem: `${t("case_metrics")} (${t("optional")})`,
      render: () => (
        <Tab.Pane>
          <Form>
            <Form.Group widths="equal">
              <Form.Input label={t("estimated_lost_time")} value={form.metrics.estimatedLostTime}
                onChange={e => setMetrics("estimatedLostTime", e.target.value)} placeholder="hh:mm" />
              <Form.Input label={t("direct_costs")} type="number" value={form.metrics.directCosts} min={0}
                onChange={e => setMetrics("directCosts", Number(e.target.value))} />
              <Form.Input label={t("indirect_costs")} type="number" value={form.metrics.indirectCosts} min={0}
                onChange={e => setMetrics("indirectCosts", Number(e.target.value))} />
              <Form.Input label={t("confirmed_lost_days")} type="number" value={form.metrics.confirmedLostDays} min={0}
                onChange={e => setMetrics("confirmedLostDays", Number(e.target.value))} />
            </Form.Group>
          </Form>
        </Tab.Pane>
      ),
    },
  ];

  return (
    <div className="accident-form-container">
      <ToastContainer position="top-center" autoClose={800} hideProgressBar />

      {accident?.customId && (
        <div style={{ textAlign: "right", marginBottom: "0.5rem" }}>
          <Label color="blue">ID: {accident.customId}</Label>
        </div>
      )}

      <Tab panes={panes} menu={{ pointing: true, secondary: true, className: "af-tab-menu" }} />

      {/* ── BOTONES ──────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "center", gap: "0.5rem", marginTop: "1.5rem", paddingBottom: "1rem" }}>
        <Button primary loading={saving} disabled={saving} onClick={handleSubmit} style={{ minWidth: "10rem" }}>
          {accident ? t("update") : t("create")}
        </Button>
        <Button type="button" onClick={close} style={{ minWidth: "10rem" }}>{t("cancel")}</Button>
      </div>
    </div>
  );
}
